import { type FC } from 'react';

import { type DefaultPageProps } from '@core/blocks/edu-flow/components/Page/DefaultPage';
import FaceliftAdditionalContentFields from '@core/blocks/facelift/components/AdditionalContentFields';
import InfoPanelMobile from '@core/blocks/facelift/components/AdditionalContentFields/InfoPanel/Mobile';
import FaceliftFlowStatCta from '@core/blocks/facelift/components/AdditionalContentFields/StatCta';
import FaceliftBackButton from '@core/blocks/facelift/components/NavigationButtons/BackButton';
import FaceliftConintueButton from '@core/blocks/facelift/components/NavigationButtons/ContinueButton';
import ProgresBarFacelift from '@core/blocks/facelift/components/ProgressBar';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useEmbeddedVoyagerObserver from '@core/hooks/useEmbeddedVoyagerObserver';
import cn from '@core/utils/classNames';

import EduFlow2AdditionalContentFields from '../AdditionalContentFields';
import EduFlow2StatCta from '../AdditionalContentFields/StatCta';
import { AdditionalContentFieldType } from '../AdditionalContentFields/types';
import Fields from '../Fields';
import EduFlow2BackButton from '../NavigationButtons/BackButton';
import EduFlow2ContinueButton from '../NavigationButtons/ContinueButton';
import ProgressBarEduFlow2 from '../ProgressBar';
import PageHeader from '../QuestionHeader';
import styles from './Page.module.css';

const Page: FC<DefaultPageProps> = ({
  flow,
  page,
  prevPage,
  onPreviousPage,
  showContinueButton,
  buttonRef,
  question,
  fieldErrors,
  onContinue,
  getFieldsValidationErrors,
}) => {
  const { heading, preSubheading, postSubheading, bottomDisclaimer } = question;

  const { isEmbeddedVoyager } = useEmbeddedVoyagerObserver();

  const showProgressBar = flow && page && question.hideProgressBarAndBackButton !== true;
  const showNextQuestionButton =
    question.showNextQuestionButton !== false && showContinueButton && question.showContinueButton !== false;

  const showPreviousQuestionButton = question.showPreviousButton !== false && prevPage;

  const flags = useFeatureFlags();

  const isFaceliftTest = flags?.facelift === 'test';

  const isSecurityBoostTest = !!flags?.securityBoost;

  const AdditionalContentFieldComponent = isFaceliftTest
    ? FaceliftAdditionalContentFields
    : EduFlow2AdditionalContentFields;

  const StatCtaComponent = isFaceliftTest ? FaceliftFlowStatCta : EduFlow2StatCta;

  const pageContentStyles = isFaceliftTest ? styles.faceLiftPageContent : styles.pageContent;

  const pagesContentContainerStyles = isFaceliftTest
    ? styles.faceliftPageContentContainer
    : styles.pageContentContainer;

  const pageSidebarContainerStyles = isFaceliftTest ? styles.faceliftPageSidebarContainer : styles.pageSidebarContainer;

  const infoPanel = question?.additionalContent?.find(({ type }) => type === AdditionalContentFieldType.infoPanel);

  const ProgressBar = isFaceliftTest ? ProgresBarFacelift : ProgressBarEduFlow2;

  const ContinueButtonProps = {
    type: question?.continueButton?.type,
    text: question?.continueButton?.ctaLabel ?? 'Next Question',
    disabled: !showContinueButton,
    onClick: onContinue,
    buttonRef,
    ctaLabel: question?.continueButton?.ctaLabel,
    popupContent: question?.continueButton?.popupContent,
    popupCtaLabel: question?.continueButton?.popupCtaLabel,
    popupExitButtonName: question?.continueButton?.popupExitButtonName,
    heading: question?.continueButton?.heading,
    validateFields: getFieldsValidationErrors,
  };

  if (flags?.loading) return null;

  return (
    <div className={styles.page}>
      {prevPage && question.hideProgressBarAndBackButton !== true && !isFaceliftTest && (
        <div data-testid="backbutton" className={styles.backButtonContainer}>
          <EduFlow2BackButton onClick={onPreviousPage} />
        </div>
      )}
      <div className={`${pagesContentContainerStyles} ${isEmbeddedVoyager && styles.emmbedded}`}>
        <div className={cn(pageContentStyles, isEmbeddedVoyager && styles.embeddedContent)}>
          <div className={styles.actionArea}>
            {showProgressBar && (
              <div data-testid="progressbar" className={styles.progressBarContainer}>
                {!isFaceliftTest && (
                  <div className={styles.mobileBack}>
                    <EduFlow2BackButton onClick={onPreviousPage} />
                  </div>
                )}

                <ProgressBar flow={flow} page={page} />
              </div>
            )}
            <PageHeader heading={heading} preSubheading={preSubheading} postSubheading={postSubheading} />
            {isFaceliftTest && infoPanel && <InfoPanelMobile field={infoPanel} />}
            <Fields
              continueButtonRef={buttonRef}
              showContinueButton={showContinueButton}
              onContinue={onContinue}
              onPrevious={onPreviousPage}
              fields={question.fields}
              heading={question.heading}
              errors={fieldErrors}
            />

            {!isSecurityBoostTest && (
              <div data-testid="next-question-button-container" className={styles.submitButtonContainer}>
                {bottomDisclaimer && (
                  <div className={styles.bottomDisclaimer} dangerouslySetInnerHTML={{ __html: bottomDisclaimer }} />
                )}
                {!isFaceliftTest && showNextQuestionButton && <EduFlow2ContinueButton {...ContinueButtonProps} />}
              </div>
            )}

            {isFaceliftTest && (
              <div className={styles.faceliftButtonsDesktop}>
                {showPreviousQuestionButton && <FaceliftBackButton onClick={onPreviousPage} />}

                <FaceliftConintueButton {...ContinueButtonProps} />
              </div>
            )}
          </div>
        </div>
      </div>
      {question.hideSidebar !== true && !isEmbeddedVoyager && (
        <div className={pageSidebarContainerStyles}>
          <div className={styles.pageSidebar}>
            {question.additionalContent ? (
              <AdditionalContentFieldComponent fields={question.additionalContent} />
            ) : (
              <StatCtaComponent />
            )}
            {question.sideDisclaimer && (
              <div className={styles.sideDisclaimer} dangerouslySetInnerHTML={{ __html: question.sideDisclaimer }} />
            )}
          </div>
        </div>
      )}
      {isFaceliftTest && (
        <div className={cn(styles.faceliftButtonsMobile, isSecurityBoostTest && styles.stickyButtons)}>
          {showPreviousQuestionButton && <FaceliftBackButton onClick={onPreviousPage} />}

          <FaceliftConintueButton {...ContinueButtonProps} />
        </div>
      )}
    </div>
  );
};

export default Page;
